import { createAuthClient } from "better-auth/react";
import { twoFactorClient, usernameClient } from "better-auth/client/plugins";
import { apiHttpOrigin } from "@/lib/env";

/** Better Auth React client against apps/api (`/api/auth`): signing in and out only. Who may do
 * what is the policy's, asked through the API; the rest of `/api/auth` is closed
 * (auth-paths.ts). */
export const authClient = createAuthClient({
  baseURL: apiHttpOrigin,
  plugins: [usernameClient(), twoFactorClient()],
  fetchOptions: { credentials: "include" },
});

export const { signOut, useSession } = authClient;

export async function signIn(identifier: string, password: string) {
  const looksLikeEmail = identifier.includes("@");
  const result = looksLikeEmail
    ? await authClient.signIn.email({ email: identifier, password })
    : await authClient.signIn.username({ username: identifier, password });
  return result;
}

/** True when the password was right and the session waits on the second-factor code. */
export function needsSecondFactor(result: Awaited<ReturnType<typeof signIn>>): boolean {
  const data = result.data as { twoFactorRedirect?: boolean } | null | undefined;
  return data?.twoFactorRedirect === true;
}

/** Works while enrolling (signed in) and while signing in (holding the password challenge); the
 * server limits how often. */
export async function sendSecondFactorCode(): Promise<{
  error?: { message: string };
}> {
  const result = await authClient.twoFactor.sendOtp();
  if (result.error) {
    return {
      error: {
        message:
          result.error.status === 429
            ? "A code was just sent. Wait a moment before asking for another."
            : "Could not send a code. Try again in a moment.",
      },
    };
  }
  return {};
}

/** Which phone or inbox the code went to, for the page that has no session yet. Null when the
 * server cannot say. */
export async function secondFactorDestination(): Promise<{
  channel: "sms" | "email";
  to: string;
} | null> {
  const response = await fetch(`${apiHttpOrigin}/api/auth/two-factor/where`, {
    credentials: "include",
  }).catch(() => null);
  if (!response?.ok) return null;
  const body = (await response.json().catch(() => null)) as {
    channel?: "sms" | "email" | null;
    to?: string | null;
  } | null;
  return body?.channel && body.to ? { channel: body.channel, to: body.to } : null;
}

const TOO_MANY = "Too many attempts. Wait a minute, then try again.";

/** What a refused sign-in says: a locked account in the server's words, any other ceiling as a
 * pause. */
export function refusalWords(
  error: { status: number; code?: string; message?: string },
  fallback: string,
): string {
  if (error.code === "ACCOUNT_LOCKED") return error.message ?? TOO_MANY;
  if (error.status === 429) return TOO_MANY;
  return error.message ?? fallback;
}

// Enrolling, the first good code turns the factor on.
export async function verifySecondFactor(code: string): Promise<{ error?: { message: string } }> {
  const digits = code.replaceAll(/\D/g, "");
  const result = await authClient.twoFactor.verifyOtp({ code: digits });
  if (result.error) {
    return {
      error: {
        message:
          result.error.status === 429
            ? refusalWords(result.error, TOO_MANY)
            : result.error.code === "OTP_HAS_EXPIRED"
              ? "That code has expired. Send a new one and try again."
              : result.error.code === "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE"
                ? "Too many wrong tries for that code. Send a new one and try again."
                : "That code was not accepted. Check the message and try again.",
      },
    };
  }
  return {};
}

// The API's `facilityPin` plugin owns the rule (building IP allowlist, 30-day expiry). No client
// plugin, so called directly; `credentials` is needed because the response sets the session cookie.
export async function signInWithPin(code: string): Promise<{ error?: { message: string } }> {
  const response = await fetch(`${apiHttpOrigin}/api/auth/sign-in/pin`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (response.ok) return {};
  const body = (await response.json().catch(() => null)) as {
    message?: string;
  } | null;
  return { error: { message: body?.message ?? "That code was not accepted." } };
}

// The key in Sarah's hand-off text signs the person in before the guard shows a sign-in form.
// `next` comes from the server's row for the key, not from the URL. Called directly like the code
// sign-in.
export async function signInWithLoginKey(key: string): Promise<{ next?: string } | null> {
  const response = await fetch(`${apiHttpOrigin}/api/auth/sign-in/link`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key }),
  });
  if (!response.ok) return null;
  return (await response.json().catch(() => null)) as { next?: string } | null;
}
